<template>
    <div
        class="snackbar-container"
        :class="{
            hidden: !shown,
            'top-right': currentPosition === 'right',
            'top-center': currentPosition === 'center',
            'top-left': currentPosition === 'left',
        }"
        :style="{ '--snackbar-slide': slideOffset }"
    >
        <Transition name="snackbar" @after-leave="onAfterLeave">
            <div v-if="shown" class="snackbar" :class="currentType" @mouseenter="onHover(true)" @mouseleave="onHover(false)">
                <i class="snackbar-icon fas" :class="iconClass"></i>
                <span class="snackbar-message">{{ currentMessage }}</span>
                <button v-if="currentAction" type="button" class="snackbar-action" @click="onAction">
                    {{ currentAction.label }}
                </button>
                <button v-if="closable" type="button" class="snackbar-close" :title="t('Close')" @click="hide">
                    <i class="fas fa-times"></i>
                </button>
            </div>
        </Transition>
    </div>
</template>

<script setup lang="ts">
import { onBeforeUnmount, ref } from "vue";
import { useAppEventListener } from "@/composables/useAppEventListener";
import type { SnackBarOptions } from "@/control/app-events";

defineOptions({ name: "SnackBar" });

interface QueuedItem {
    message: string;
    type: "success" | "error" | "warning" | "info";
    duration: number;
    position: "left" | "center" | "right";
    action?: { label: string; callback: () => void };
    closable: boolean;
}

const shown = ref(false);
const currentMessage = ref("");
const currentType = ref<"success" | "error" | "warning" | "info">("info");
const currentPosition = ref<"left" | "center" | "right">("right");
const currentAction = ref<{ label: string; callback: () => void } | undefined>(undefined);
const closable = ref(true);

const queue = ref<QueuedItem[]>([]);
let autoHideTimer: ReturnType<typeof setTimeout> | null = null;
let remainingTime = 0;
let startTime = 0;
let leaving = false;

const slideOffset = ref("100%");

const updateSlideOffset = (pos: "left" | "center" | "right") => {
    switch (pos) {
        case "left":
            slideOffset.value = "-100%";
            break;
        case "center":
            slideOffset.value = "0%";
            break;
        default:
            slideOffset.value = "100%";
            break;
    }
};
const iconClassMap: Record<string, string> = {
    success: "fa-check-circle",
    error: "fa-exclamation-circle",
    warning: "fa-exclamation-triangle",
    info: "fa-info-circle",
};

const iconClass = ref("fa-info-circle");

const parseOptions = (msgOrOptions: string | SnackBarOptions): QueuedItem => {
    if (typeof msgOrOptions === "string") {
        return {
            message: msgOrOptions,
            type: "info",
            duration: 3000,
            position: "right",
            closable: true,
        };
    }
    const duration = msgOrOptions.duration ?? 3000;
    let closable = msgOrOptions.closable ?? true;
    if (duration === 0 && !closable) {
        closable = true;
    }
    return {
        message: msgOrOptions.message,
        type: msgOrOptions.type || "info",
        duration,
        position: msgOrOptions.position || "right",
        action: msgOrOptions.action,
        closable,
    };
};

const showNext = () => {
    if (autoHideTimer) {
        clearTimeout(autoHideTimer);
        autoHideTimer = null;
    }

    if (queue.value.length === 0) {
        return;
    }

    const item = queue.value.shift()!;
    currentMessage.value = item.message;
    currentType.value = item.type;
    currentPosition.value = item.position;
    updateSlideOffset(item.position);
    currentAction.value = item.action;
    closable.value = item.closable;
    iconClass.value = iconClassMap[item.type] || "fa-info-circle";

    shown.value = true;
    remainingTime = item.duration;
    startTime = Date.now();

    if (item.duration > 0) {
        autoHideTimer = setTimeout(() => {
            hide();
        }, item.duration);
    }
};

const show = (msgOrOptions: string | SnackBarOptions) => {
    const item = parseOptions(msgOrOptions);
    queue.value.push(item);

    if (!shown.value && !leaving) {
        showNext();
    }
};

const hide = () => {
    if (autoHideTimer) {
        clearTimeout(autoHideTimer);
        autoHideTimer = null;
    }
    leaving = true;
    shown.value = false;
};

const onAfterLeave = () => {
    leaving = false;
    showNext();
};

const onAction = () => {
    if (currentAction.value) {
        currentAction.value.callback();
    }
    hide();
};

const onHover = (entering: boolean) => {
    if (entering && autoHideTimer) {
        const elapsed = Date.now() - startTime;
        remainingTime = Math.max(0, remainingTime - elapsed);
        clearTimeout(autoHideTimer);
        autoHideTimer = null;
    } else if (!entering && remainingTime > 0 && shown.value) {
        startTime = Date.now();
        autoHideTimer = setTimeout(() => {
            hide();
        }, remainingTime);
    }
};

useAppEventListener("snack", show);

onBeforeUnmount(() => {
    if (autoHideTimer) {
        clearTimeout(autoHideTimer);
        autoHideTimer = null;
    }
});
</script>

<style></style>
